import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "../api/client";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";

export default function VerifyPendingPage() {
  const { t } = useTranslation("auth");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const initialEmail = (location.state?.email || user?.email || "").trim();
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // P0 2026-07-15 — Wall verschwindet automatisch, sobald verifiziert.
  useEffect(() => {
    if (user?.email_verified === true) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!email && user?.email) setEmail(user.email);
  }, [user, email]);

  const resend = async () => {
    if (!email) {
      setFeedback({ kind: "err", text: t("verify.enterEmail") });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const { data } = await apiClient.post("/auth/resend-verification", { email });
      if (data?.sent === false) {
        setFeedback({ kind: "warn", text: t("verify.notConfigured") });
      } else {
        setFeedback({ kind: "ok", text: t("verify.resendGeneric") });
      }
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (code === "email_service_not_configured") {
        setFeedback({ kind: "warn", text: t("verify.notConfigured") });
      } else {
        setFeedback({ kind: "err", text: formatApiError(err) });
      }
    } finally {
      setSending(false);
    }
  };

  // „Ich habe bestätigt" → /auth/me neu laden; verifiziert → Dashboard.
  const checkConfirmed = async () => {
    setChecking(true);
    setFeedback(null);
    try {
      const fresh = await refreshUser();
      if (fresh?.email_verified === true) {
        navigate("/dashboard", { replace: true });
      } else {
        setFeedback({ kind: "warn", text: t("verify.notYetVerified") });
      }
    } finally {
      setChecking(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="mx-auto max-w-md py-12" data-testid="verify-pending-page">
      <div className="rounded-3xl border border-zinc-800 bg-zinc-900/40 p-8 text-center space-y-4">
        <div className="text-emerald-400 text-5xl">📬</div>
        <h1 className="text-2xl font-semibold text-zinc-100">{t("verify.pendingTitle")}</h1>
        <p className="text-sm text-zinc-400 leading-relaxed">
          {email ? (
            <>
              {t("verify.pendingBodyWithEmailA")} <span className="text-zinc-200">{email}</span>{" "}
              {t("verify.pendingBodyWithEmailB")}
            </>
          ) : (
            t("verify.pendingBodyNoEmail")
          )}
        </p>
        <p className="text-xs text-zinc-500">
          {t("verify.spamHint")}
        </p>

        <div className="space-y-2 pt-4 border-t border-zinc-800">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="deine@email.com"
            data-testid="verify-pending-email-input"
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={resend}
            disabled={sending}
            data-testid="verify-pending-resend-btn"
            className="w-full rounded-md border border-emerald-500/60 px-4 py-2 text-sm font-medium text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-60"
          >
            {sending ? t("verify.sending") : t("verify.resendCta")}
          </button>
          <button
            type="button"
            onClick={checkConfirmed}
            disabled={checking}
            data-testid="verify-pending-confirmed-btn"
            className="w-full rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
          >
            {checking ? t("verify.checking") : t("verify.confirmedCta")}
          </button>
        </div>

        {feedback && (
          <div
            data-testid="verify-pending-feedback"
            className={`rounded-md border px-3 py-2 text-xs ${
              feedback.kind === "ok"
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : feedback.kind === "warn"
                ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                : "border-rose-900 bg-rose-950/40 text-rose-300"
            }`}
          >
            {feedback.text}
          </div>
        )}

        <div className="pt-4 flex items-center justify-center gap-4 text-xs text-zinc-500">
          {user ? (
            <button
              onClick={onLogout}
              data-testid="verify-pending-logout-btn"
              className="hover:text-zinc-300 underline-offset-4 hover:underline"
            >
              {t("verify.logoutCta")}
            </button>
          ) : (
            <Link to="/login" className="hover:text-zinc-300 underline-offset-4 hover:underline">
              {t("verify.toLogin")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
